import React, { useState, useEffect } from 'react'
import Button from '@SiteComponents/Button'
import classnames from 'classnames'
import Logout from '@Images/user_profile_logout.svg'
import Error from '@Images/user_profile_error.svg'

import {
  sanitizeEmail,
  isValidEmail,
  EMAIL_MAX_LENGTH,
} from '@Utils/EmailSanitizerAndValidator'

const DEFAULT_PASSWORD_LENGTH = 9
const PASSWORD_MIN_LENGTH = 6
const PASSWORD_MAX_LENGTH = 100

import './SecuritySection.scss'

interface SecuritySectionProps {
  readonly userEmail: string
  readonly saving: boolean
  readonly hasUpdatedProfileSuccessfully: boolean
  readonly errorMessageCurrentPasswordForNewEmailFromBackend: string | null
  readonly errorMessageCurrentPasswordForNewPasswordFromBackend: string | null
  handleLogout(): void
  handleSaveEditedEmail(email: string, currentPassword: string): void
  handleSaveEditedPassword(password: string, currentPassword: string): void
}

const SecuritySection: React.FC<SecuritySectionProps> = props => {
  const {
    userEmail,
    saving,
    hasUpdatedProfileSuccessfully,
    errorMessageCurrentPasswordForNewEmailFromBackend,
    errorMessageCurrentPasswordForNewPasswordFromBackend
  } = props

  const [showEditEmailButton, setShowEditEmailButton] = useState(false)
  const [showEditPasswordButton, setShowEditPasswordButton] = useState(false)
  const [editingEmail, setEditingEmail] = useState(false)
  const [editingPassword, setEditingPassword] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [currentPasswordForNewEmail, setCurrentPasswordForNewEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [currentPasswordForNewPassword, setCurrentPasswordForNewPassword] = useState('')
  const [hasJustUpdatedEmail, setHasJustUpdatedEmail] = useState(false)
  const [hasJustUpdatedPassword, setHasJustUpdatedPassword] = useState(false)
  const [errorMessageNewEmail, setErrorMessageNewEmail] = useState<string | null>(null)
  const [errorMessageCurrentPasswordForNewEmail, setErrorMessageCurrentPasswordForNewEmail] = useState<string | null>(null)
  const [errorMessageNewPassword, setErrorMessageNewPassword] = useState<string | null>(null)
  const [errorMessageCurrentPasswordForNewPassword, setErrorMessageCurrentPasswordForNewPassword] = useState<string | null>(null)

  const sanitizedNewEmail = sanitizeEmail(newEmail)

  useEffect(() => {
    if (!saving && hasJustUpdatedEmail) {
      if (hasUpdatedProfileSuccessfully) {
        cancelEditingEmail()
      }
      setHasJustUpdatedEmail(false)
    }

    if (!saving && hasJustUpdatedPassword) {
      if (hasUpdatedProfileSuccessfully) {
        cancelEditingPassword()
      }
      setHasJustUpdatedPassword(false)
    }
  }, [saving])

  useEffect(() => {
    if (!!errorMessageCurrentPasswordForNewEmailFromBackend) {
      setErrorMessageCurrentPasswordForNewEmail(errorMessageCurrentPasswordForNewEmailFromBackend)
    }
  }, [errorMessageCurrentPasswordForNewEmailFromBackend])

  useEffect(() => {
    if (!!errorMessageCurrentPasswordForNewPasswordFromBackend) {
      setErrorMessageCurrentPasswordForNewPassword(errorMessageCurrentPasswordForNewPasswordFromBackend)
    }
  }, [errorMessageCurrentPasswordForNewPasswordFromBackend])

  const displayEditEmailButton = () => {
    setShowEditEmailButton(true)
  }

  const hideEditEmailButton = () => {
    setShowEditEmailButton(false)
  }

  const displayEditPasswordButton = () => {
    setShowEditPasswordButton(true)
  }

  const hideEditPasswordButton = () => {
    setShowEditPasswordButton(false)
  }

  const startEditingEmail = () => {
    setEditingEmail(true)
  }

  const startEditingPassword = () => {
    setEditingPassword(true)
  }

  const cancelEditingEmail = () => {
    setNewEmail('')
    setCurrentPasswordForNewEmail('')
    setErrorMessageNewEmail(null)
    setErrorMessageCurrentPasswordForNewEmail(null)
    setEditingEmail(false)
  }

  const cancelEditingPassword = () => {
    setNewPassword('')
    setCurrentPasswordForNewPassword('')
    setErrorMessageNewPassword(null)
    setErrorMessageCurrentPasswordForNewPassword(null)
    setEditingPassword(false)
  }

  const saveEditedEmail = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (sanitizedNewEmail.length === 0) {
      setErrorMessageNewEmail('Email is required')
      return
    }

    if (!isValidEmail(sanitizedNewEmail)) {
      setErrorMessageNewEmail(
        sanitizedNewEmail.length > EMAIL_MAX_LENGTH
          ? `Email must not exceed ${EMAIL_MAX_LENGTH} characters.`
          : 'Please enter a valid email address'
      )
      return
    }

    if (currentPasswordForNewEmail.length === 0) {
      setErrorMessageCurrentPasswordForNewEmail('Current password is required')
      return
    }

    props.handleSaveEditedEmail(sanitizedNewEmail, currentPasswordForNewEmail)
    setHasJustUpdatedEmail(true)
  }

  const saveEditedPassword = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    setErrorMessageNewPassword(null)
    setErrorMessageCurrentPasswordForNewPassword(null)

    if (newPassword.length < PASSWORD_MIN_LENGTH) {
      setErrorMessageNewPassword(`Password must have at least ${PASSWORD_MIN_LENGTH} characters`)
      return
    }

    if (newPassword.length > PASSWORD_MAX_LENGTH) {
      setErrorMessageNewPassword(`Password must not exceed ${PASSWORD_MAX_LENGTH} characters.`)
      return
    }

    if (currentPasswordForNewPassword.length === 0) {
      setErrorMessageCurrentPasswordForNewPassword('Current password is required')
      return
    }

    props.handleSaveEditedPassword(newPassword, currentPasswordForNewPassword)
    setHasJustUpdatedPassword(true)
  }

  const changeNewEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (errorMessageNewEmail) {
      setErrorMessageNewEmail(null)
    }

    setNewEmail(e.target.value)
  }

  const changeCurrentPasswordForNewEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (errorMessageCurrentPasswordForNewEmail) {
      setErrorMessageCurrentPasswordForNewEmail(null)
    }

    setCurrentPasswordForNewEmail(e.target.value)
  }

  const changeNewPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (errorMessageNewPassword) {
      setErrorMessageNewPassword(null)
    }

    setNewPassword(e.target.value)
  }

  const changeCurrentPasswordForNewPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (errorMessageCurrentPasswordForNewPassword) {
      setErrorMessageCurrentPasswordForNewPassword(null)
    }

    setCurrentPasswordForNewPassword(e.target.value)
  }

  const renderHeaderSecuritySection = () => {
    return (
      <div className="UserProfile__header">
        <div className="UserProfile__header-title">
          Security
        </div>
        <div className="UserProfile__header-explanation">
          Customize all your information
        </div>
      </div>
    )
  }

  const renderEmail = () => {
    return (
      <React.Fragment>
        {editingEmail ? renderEmailInput() : (
          <div
            className="SecuritySection__email"
            onMouseEnter={displayEditEmailButton}
            onMouseLeave={hideEditEmailButton}
          >
            <div className="SecuritySection__label">
              E-mail
            </div>
            <div className="SecuritySection__content">
              {userEmail}
            </div>
            {renderEmailButtons()}
          </div>
        )}
      </React.Fragment>
    )
  }

  const renderEmailInput = () => {
    return (
      <form className="SecuritySection__input-wrapper" onSubmit={saveEditedEmail} noValidate>
        <input
          type="email"
          name="username"
          autoComplete="username"
          value={userEmail}
          readOnly
          hidden
          data-testid="profile-email-current-username-input"
        />
        <div className="SecuritySection__input-section">
          <div className="SecuritySection__input-label">
            Email
          </div>
          <div className="SecuritySection__input-current">
            {userEmail}
          </div>
        </div>
        <div className="SecuritySection__input-section">
          <label className="SecuritySection__input-label" htmlFor="profile-new-email">
            New email
          </label>
          <div>
            <input
              id="profile-new-email"
              name="profile-new-email"
              className={classnames(
                'UserProfile__input',
                { 'UserProfile__input-error': !!errorMessageNewEmail }
              )}
              value={newEmail}
              onChange={changeNewEmail}
              type="email"
              placeholder="Enter new email"
              autoComplete="email"
              data-testid="profile-new-email-input"
              autoFocus
            />
            {!!errorMessageNewEmail && renderErrorMessage(errorMessageNewEmail)}
          </div>
        </div>
        <div className="SecuritySection__input-section">
          <label className="SecuritySection__input-label" htmlFor="profile-email-current-password">
            Current password
          </label>
          <div>
            <input
              id="profile-email-current-password"
              name="profile-email-current-password"
              className={classnames(
                'UserProfile__input',
                { 'UserProfile__input-error': !!errorMessageCurrentPasswordForNewEmail }
              )}
              value={currentPasswordForNewEmail}
              onChange={changeCurrentPasswordForNewEmail}
              type="password"
              placeholder="Enter current password"
              autoComplete="current-password"
              data-testid="profile-email-current-password-input"
            />
            {!!errorMessageCurrentPasswordForNewEmail && renderErrorMessage(errorMessageCurrentPasswordForNewEmail)}
          </div>
        </div>
        {renderEmailButtons()}
      </form>
    )
  }

  const renderEmailButtons = () => {
    return (
      <div className="UserProfile__buttons">
        {editingEmail && (
          <Button
            styleType="gray"
            size="small"
            caption="Cancel"
            onClick={cancelEditingEmail}
          />
        )}
        {editingEmail && (
          <Button
            styleType="brand"
            size="small"
            caption="Save"
            type="submit"
            disabled={saving || !!errorMessageNewEmail || !!errorMessageCurrentPasswordForNewEmail}
            data-testid="profile-email-save"
          />
        )}
        {!editingEmail && showEditEmailButton && (
          <Button
            styleType="gray"
            size="small"
            caption="Edit"
            onClick={startEditingEmail}
          />
        )}
      </div>
    )
  }

  const renderPassword = () => {
    return (
      <React.Fragment>
        {editingPassword ? renderPasswordInput() : (
          <div
            className="SecuritySection__password"
            onMouseEnter={displayEditPasswordButton}
            onMouseLeave={hideEditPasswordButton}
          >
            <div className="SecuritySection__label">
              Password
            </div>
            <div className="SecuritySection__content">
              {'•'.repeat(DEFAULT_PASSWORD_LENGTH)}
            </div>
            {renderPasswordButtons()}
          </div>
        )}
      </React.Fragment>
    )
  }

  const renderPasswordInput = () => {
    return (
      <form className="SecuritySection__input-wrapper" onSubmit={saveEditedPassword} noValidate>
        <input
          type="email"
          name="username"
          autoComplete="username"
          value={userEmail}
          readOnly
          hidden
          data-testid="profile-password-current-username-input"
        />
        <div className="SecuritySection__input-section">
          <label className="SecuritySection__input-label" htmlFor="profile-password-current-password">
            Current password
          </label>
          <div>
            <input
              id="profile-password-current-password"
              name="profile-password-current-password"
              className={classnames(
                'UserProfile__input',
                { 'UserProfile__input-error': !!errorMessageCurrentPasswordForNewPassword }
              )}
              value={currentPasswordForNewPassword}
              onChange={changeCurrentPasswordForNewPassword}
              type="password"
              placeholder="Enter current password"
              autoComplete="current-password"
              data-testid="profile-password-current-password-input"
              autoFocus
            />
            {!!errorMessageCurrentPasswordForNewPassword && renderErrorMessage(errorMessageCurrentPasswordForNewPassword)}
          </div>
        </div>
        <div className="SecuritySection__input-section">
          <label className="SecuritySection__input-label" htmlFor="profile-new-password">
            New password
          </label>
          <div>
            <input
              id="profile-new-password"
              name="profile-new-password"
              className={classnames(
                'UserProfile__input',
                { 'UserProfile__input-error': !!errorMessageNewPassword }
              )}
              value={newPassword}
              onChange={changeNewPassword}
              type="password"
              placeholder="Enter new password"
              autoComplete="new-password"
              data-testid="profile-new-password-input"
            />
            {!!errorMessageNewPassword && renderErrorMessage(errorMessageNewPassword)}
          </div>
        </div>
        {renderPasswordButtons()}
      </form>
    )
  }

  const renderPasswordButtons = () => {
    return (
      <div className="UserProfile__buttons">
        {editingPassword && (
          <Button
            styleType="gray"
            size="small"
            caption="Cancel"
            onClick={cancelEditingPassword}
          />
        )}
        {editingPassword && (
          <Button
            styleType="brand"
            size="small"
            caption="Save"
            type="submit"
            disabled={saving || !!errorMessageNewPassword || !!errorMessageCurrentPasswordForNewPassword}
            data-testid="profile-password-save"
          />
        )}
        {!editingPassword && showEditPasswordButton && (
          <Button
            styleType="gray"
            size="small"
            caption="Edit"
            onClick={startEditingPassword}
          />
        )}
      </div>
    )
  }

  const renderErrorMessage = (message: string) => {
    return (
      <div className="UserProfile__error-message">
        <Error />
        {message}
      </div>
    )
  }

  const renderLogout = () => {
    return (
      <div className="SecuritySection__logout">
        <Button
          styleType="danger"
          size="small"
          caption="Log out"
          icon={<Logout />}
          onClick={props.handleLogout}
        />
      </div>
    )
  }

  return (
    <div className="UserProfile__section">
      {renderHeaderSecuritySection()}
      <div className="SecuritySection">
        {renderEmail()}
        {renderPassword()}
        {renderLogout()}
      </div>
    </div>
  )
}

export default SecuritySection
